import { supabase } from '../lib/supabase';

export const subscriptionService = {
  // Get current user subscription info
  async getSubscription() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('plan, subscription_status, current_period_end, stripe_customer_id, stripe_subscription_id, plan_updated_at')
        ?.eq('id', user?.id)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create Stripe checkout session
  async createCheckoutSession() {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
        body: {}
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create Stripe customer portal session
  async createPortalSession() {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: {}
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Check if user has Pro access
  hasProAccess(profile) {
    if (!profile) return false;
    
    const isPro = profile?.plan === 'PRO';
    const isActive = ['active', 'trialing']?.includes(profile?.subscription_status);
    const notExpired = !profile?.current_period_end || new Date(profile?.current_period_end) > new Date();
    
    return isPro && isActive && notExpired;
  },

  // Feature limits for FREE plan
  FREE_LIMITS: {
    MAX_ACCOUNTS: 3,
    MAX_CHEQUING: 1,
    MAX_LIABILITY: 1,
    MAX_SAVINGS: 1,
    MAX_CASH: 1,
    MAX_RECURRING: 1,
    MAX_BUDGETS: 1,
    MAX_ALERTS: 1
  },

  // Check if user can perform action based on plan
  async canUse(feature, currentCount = 0) {
    try {
      const { data: subscription } = await this.getSubscription();
      
      // Pro users have unlimited access
      if (this.hasProAccess(subscription)) {
        return { allowed: true, limit: null, current: currentCount };
      }

      // Check FREE limits
      const limits = this.FREE_LIMITS;
      let limit = null;
      let allowed = true;

      switch (feature) {
        case 'accounts':
          limit = limits?.MAX_ACCOUNTS;
          allowed = currentCount < limit;
          break;
        case 'recurring':
          limit = limits?.MAX_RECURRING;
          allowed = currentCount < limit;
          break;
        case 'budgets':
          limit = limits?.MAX_BUDGETS;
          allowed = currentCount < limit;
          break;
        case 'alerts':
          limit = limits?.MAX_ALERTS;
          allowed = currentCount < limit;
          break;
        case 'reports': case'transfers': case'split': case'reconcile': case'rules': case'exports':
          // Pro-only features
          allowed = false;
          limit = 0;
          break;
        default:
          allowed = true;
          limit = null;
      }

      return { allowed, limit, current: currentCount };
    } catch (error) {
      console.error('Feature check error:', error);
      return { allowed: false, limit: null, current: currentCount, error };
    }
  },

  // Get account type limits for FREE plan
  async getAccountTypeLimits() {
    try {
      const { data: subscription } = await this.getSubscription();
      
      // Pro users have unlimited
      if (this.hasProAccess(subscription)) {
        return {
          unlimited: true,
          limits: null
        };
      }

      // FREE plan limits
      return {
        unlimited: false,
        limits: {
          'Chequing': this.FREE_LIMITS?.MAX_CHEQUING,
          'Savings': this.FREE_LIMITS?.MAX_SAVINGS,
          'Credit Card': this.FREE_LIMITS?.MAX_LIABILITY,
          'Line of Credit': this.FREE_LIMITS?.MAX_LIABILITY,
          'Loan': this.FREE_LIMITS?.MAX_LIABILITY,
          'Cash': this.FREE_LIMITS?.MAX_CASH
        }
      };
    } catch (error) {
      console.error('Account type limits error:', error);
      return { unlimited: false, limits: null, error };
    }
  },

  // Format subscription status for display
  formatStatus(status) {
    const statusMap = {
      'active': 'Active',
      'trialing': 'Trial',
      'past_due': 'Past Due',
      'canceled': 'Canceled',
      'incomplete': 'Incomplete',
      'unpaid': 'Unpaid',
      'inactive': 'Inactive'
    };
    return statusMap?.[status] || status;
  },

  // Format plan name for display
  formatPlan(plan) {
    return plan === 'PRO' ? 'Pro' : 'Free';
  },

  // Demo account plan toggle (only for demo@rovynbudget.com)
  async toggleDemoPlan(userEmail) {
    try {
      // Security check: only allow for demo account
      if (userEmail !== 'demo@rovynbudget.com') {
        return { data: null, error: { message: 'Unauthorized: This feature is only available for the demo account' } };
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      // Get current plan
      const { data: currentProfile, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('plan, subscription_status')
        ?.eq('id', user?.id)
        ?.single();

      if (fetchError) throw fetchError;

      // Toggle plan
      const newPlan = currentProfile?.plan === 'PRO' ? 'FREE' : 'PRO';
      const newStatus = newPlan === 'PRO' ? 'active' : 'inactive';
      const newPeriodEnd = newPlan === 'PRO' 
        ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toISOString() // 1 year from now
        : null;

      // Update profile
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          plan: newPlan,
          subscription_status: newStatus,
          current_period_end: newPeriodEnd,
          plan_updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};